import styled from 'styled-components';

import { colors } from 'constants/colors';

import { GetDeliveryQuery } from 'data/__generated__';

import { formatDateAsAnniversary } from '../../../../helpers/dateTimes';
import { getDeliveryNoteTotemLabel } from '../../../../helpers/totemLabel';

export const PageHeader = ({ delivery }: { delivery: NonNullable<GetDeliveryQuery['delivery']> }) => {
    const { deliveryDate, organizationName, totem, orders, siteId, siteName } = delivery;

    // Determine if this is a freefood delivery (with totem and orders) or a store delivery (with siteId)
    const isFreefoodDelivery = totem && orders.length > 0;
    const isStoreDelivery = siteId && siteName;

    if (!isFreefoodDelivery && !isStoreDelivery) {
        throw new Error(
            'Impossible de générer un bon de livraison. Cette livraison doit être liée à une commande ou à un site.',
        );
    }

    return (
        <OuterContainer>
            <Content>
                <DetailsTable>
                    <thead>
                        <tr>
                            <HeaderCell>TOTEM SAS</HeaderCell>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <BodyCell>Société par actions simplifiée</BodyCell>
                        </tr>
                        <tr>
                            <BodyCell>Au capital de 723,10 euros</BodyCell>
                        </tr>
                        <tr>
                            <BodyCell>Siège social : 5 avenue du Général de Gaulle, 94160 Saint-Mandé</BodyCell>
                        </tr>
                        <tr>
                            <BodyCell>825 323 496 R.C.S. Créteil</BodyCell>
                        </tr>
                    </tbody>
                </DetailsTable>
                <DetailsTable>
                    <thead>
                        <tr>
                            <HeaderCell>{isFreefoodDelivery ? 'TOTEM' : 'SITE'}</HeaderCell>
                            <HeaderCell>
                                {isFreefoodDelivery
                                    ? getDeliveryNoteTotemLabel({
                                          organizationName,
                                          number: totem?.number,
                                          name: totem?.name,
                                      })
                                    : `${siteName}${organizationName ? ` - ${organizationName}` : ''}`}
                            </HeaderCell>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <BodyCell $isBold>Date de livraison</BodyCell>
                            <BodyCell>{formatDateAsAnniversary({ dateTime: deliveryDate })}</BodyCell>
                        </tr>
                        {isFreefoodDelivery && (
                            <>
                                <tr>
                                    <BodyCell $isBold>Commande n°</BodyCell>
                                    <BodyCell>{orders[0]?.number}</BodyCell>
                                </tr>
                                <tr>
                                    <BodyCell $isBold>Adresse</BodyCell>
                                    <BodyCell>{totem?.address}</BodyCell>
                                </tr>
                                <tr>
                                    <BodyCell $isBold>Contact</BodyCell>
                                    <BodyCell>{orders[0]?.user?.profile?.fullname}</BodyCell>
                                </tr>
                            </>
                        )}
                        {isStoreDelivery && (
                            <>
                                <tr>
                                    <BodyCell $isBold>Site ID</BodyCell>
                                    <BodyCell>{siteId}</BodyCell>
                                </tr>
                            </>
                        )}
                    </tbody>
                </DetailsTable>
            </Content>
            <TitleContainer>
                <Title>Bon de livraison</Title>
            </TitleContainer>
        </OuterContainer>
    );
};

const OuterContainer = styled.div`
    display: table-header-group;
`;

const Content = styled.div`
    display: flex;
    justify-content: space-between;
    color: ${colors.black};
`;

const DetailsTable = styled.table`
    border: 1px solid ${colors.lightGrey};
    padding: 5px;

    & > :not(:first-child) {
        margin-top: 5px;
    }
`;

const HeaderCell = styled.th`
    font-size: 14px;
    text-align: left;
`;

const BodyCell = styled.td<{ $isBold?: boolean }>`
    font-size: 12px;
    font-weight: ${({ $isBold }) => ($isBold ? 800 : 500)};
`;

const TitleContainer = styled.div`
    display: flex;
    justify-content: center;
    padding: 10px;
    font-weight: 800;
`;

const Title = styled.span`
    font-size: 32px;
    color: ${colors.green};
`;
